import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Loader2, LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";

// ===== BOUTON SEUL (à mettre dans le menu) =====
interface LogoutButtonProps {
  onClick: () => void;
}

export const LogoutButton = ({ onClick }: LogoutButtonProps) => {
  const { t } = useTranslation(["common"]);
  return (
    <Button
      variant="ghost"
      onClick={onClick}
      className="w-full justify-start min-h-[44px] text-destructive hover:text-destructive hover:bg-destructive/10"
    >
      <LogOut className="h-4 w-4 mr-2" />
      {t("signOut")}
    </Button>
  );
};

// ===== DIALOG SEUL (à rendre hors du menu) =====
interface LogoutConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const LogoutConfirmDialog = ({ 
  open, 
  onOpenChange 
}: LogoutConfirmDialogProps) => {
  const { t } = useTranslation(["common"]);
  const { logout } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleConfirmLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      setIsLoggingOut(false);
      onOpenChange(false);
    }
  };

  return (
    <AlertDialog 
      open={open} 
      onOpenChange={(newOpen) => {
        if (!isLoggingOut) {
          onOpenChange(newOpen);
        }
      }}
    >
      <AlertDialogContent
        onEscapeKeyDown={(e) => {
          if (isLoggingOut) e.preventDefault();
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{t("confirmation")}</AlertDialogTitle>
          <AlertDialogDescription>
            {t("logoutConfirm")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-0">
          <AlertDialogCancel 
            disabled={isLoggingOut}
            className="min-h-[44px]"
          >
            {t("cancel")}
          </AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleConfirmLogout}
            disabled={isLoggingOut}
            className="min-h-[44px] bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isLoggingOut ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t("loggingOut")}
              </>
            ) : (
              t("signOut")
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

// ===== COMPOSANT COMBINÉ (rétrocompatibilité) =====
export const LogoutButtonWithConfirm = ({ onDialogClose }: { onDialogClose?: () => void }) => {
  const [open, setOpen] = useState(false);

  const handleClick = () => {
    onDialogClose?.();
    setTimeout(() => setOpen(true), 200);
  };

  return (
    <>
      <LogoutButton onClick={handleClick} />
      <LogoutConfirmDialog open={open} onOpenChange={setOpen} />
    </>
  );
};
